import './index.scss'

import { useEffect, useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import LoadingIndicator from '@components/LoadingIndicator'
import Modal from '@components/Modal'
import PersonCard from '@components/PersonCard'
import VolunteeringController from '@data/controller/VolunteeringController'
import VolunteerTeam, { VolunteerTeamRole } from '@data/interfaces/Volunteering/volunteerteam.interface'
import { usePeopleSearch } from '@hooks/usePeople'
import { Person, PersonAttributes } from '@interfaces/person.interface'
import RolePicker, { toggleRoleID } from '../RolePicker'

import backIcon from 'url:@icon/chevron.left.plain.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';
import closeIcon from 'url:@icon/close.svg';
import forwardIcon from 'url:@icon/arrow.right.plain.svg';

// How the first phase finds the person: an existing one, or a brand-new one.
type InviteMode = 'search' | 'create';

// Who comes first, then which of the team's roles they start off holding.
type InvitePhase = 'person' | 'roles';

// The phase being left, kept mounted long enough to slide out from under the
// one arriving.
type PhaseTransition = {
  from: InvitePhase;
  direction: 'forward' | 'back';
};

// Matches the slide in index.scss.
const PHASE_TRANSITION_MS = 240;

export default function InviteVolunteerModal(props: {
  isOpen: boolean,
  team?: VolunteerTeam | null,
  /// The team's roles, so the volunteer can start off holding some.
  roles?: VolunteerTeamRole[],
  close: () => void,
  onInvited: () => void
}) {
  const auth = useAuth();

  const [mode, setMode] = useState<InviteMode>('search');
  const [phase, setPhase] = useState<InvitePhase>('person');
  const [transition, setTransition] = useState<PhaseTransition | null>(null);
  const [query, setQuery] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleIDs, setRoleIDs] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchResults = usePeopleSearch(query);
  const isSearching = query.trim().length > 0;

  const roles = props.roles ?? [];

  // A team with no roles has nothing for a second phase to offer, so the
  // invite goes straight out from the first.
  const hasRolePhase = roles.length > 0;

  const hasInvitee = mode === 'search'
    ? selectedPerson !== null
    : name.trim().length > 0 && email.trim().length > 0;

  useEffect(() => {
    if (!transition) return;

    const timeout = window.setTimeout(() => setTransition(null), PHASE_TRANSITION_MS);

    return () => window.clearTimeout(timeout);
  }, [transition]);

  const changePhase = (nextPhase: InvitePhase) => {
    if (nextPhase === phase) return;

    setTransition({ from: phase, direction: nextPhase === 'roles' ? 'forward' : 'back' });
    setPhase(nextPhase);
    setError(null);
  };

  const startCreating = () => {
    // Whatever they typed is a reasonable starting point for the new person.
    const typed = query.trim();

    if (typed.length > 0) {
      if (typed.includes('@')) {
        setEmail(typed);
      } else {
        setName(typed);
      }
    }

    setSelectedPerson(null);
    setError(null);
    setMode('create');
  };

  const backToSearch = () => {
    setError(null);
    setMode('search');
  };

  const invite = async () => {
    if (!auth.churchId || !props.team) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = mode === 'search' && selectedPerson
        ? await VolunteeringController.addTeamMember(auth.churchId, props.team.id, selectedPerson.id, auth, roleIDs)
        : await VolunteeringController.createTeamMember(
          auth.churchId,
          props.team.id,
          { name: name.trim(), email: email.trim() } as PersonAttributes,
          auth,
          roleIDs
        );

      if (response.error) {
        setError(response.error);
        setIsSaving(false);
        return;
      }

      props.onInvited();
    } finally {
      setIsSaving(false);
    }
  };

  const isChoosingPerson = phase === 'person';

  const renderPhase = (target: InvitePhase) => {
    if (target === 'roles') {
      return <>
        <PersonCard
          name={mode === 'search' ? selectedPerson?.attributes.name ?? '' : name.trim()}
          email={(mode === 'search' ? selectedPerson?.attributes.email : email.trim()) ?? undefined}
        />

        <div className='invite-roles'>
          <p className='invite-roles-label'>Roles</p>

          <RolePicker
            roles={roles}
            selectedRoleIDs={roleIDs}
            disabled={isSaving}
            onToggle={(roleID) => setRoleIDs((currentRoleIDs) => toggleRoleID(currentRoleIDs, roleID))}
          />

          <p className='invite-hint'>Optional. Roles can be changed later from the volunteer's menu.</p>
        </div>
      </>
    }

    if (mode === 'search') {
      return <>
        <input
          id='query'
          className='invite-search'
          type='search'
          value={query}
          placeholder='Search by first name or email'
          aria-label='Search people'
          disabled={isSaving}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedPerson(null);
          }}
        />

        <SearchResults
          isSearching={isSearching}
          loading={searchResults.loading}
          people={searchResults.people}
          selectedPerson={selectedPerson}
          onSelect={setSelectedPerson}
        />

        <button type='button' className='invite-stage-switch' disabled={isSaving} onClick={startCreating}>
          Can't find who you're looking for? Create New Person
        </button>
      </>
    }

    return <>
      <div className='field-container'>
        <label htmlFor='invite-name'>Name</label>
        <input
          id='invite-name'
          type='text'
          value={name}
          placeholder='Full name'
          disabled={isSaving}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className='field-container'>
        <label htmlFor='invite-email'>Email</label>
        <input
          id='invite-email'
          type='email'
          value={email}
          placeholder='name@example.com'
          disabled={isSaving}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <button type='button' className='invite-stage-switch' disabled={isSaving} onClick={backToSearch}>
        Search for Existing Person Instead
      </button>
    </>
  };

  const confirmButton = <button
    type='button'
    className={isSaving ? 'header-control confirm saving' : 'header-control confirm'}
    disabled={!hasInvitee || isSaving}
    onClick={() => {
      void invite();
    }}>
    {isSaving ?
      <span className='header-control-spinner' role='status' aria-label='Inviting' /> :
      <img src={checkmarkIcon} alt='Invite icon' />}
  </button>;

  return <Modal
    id='invite-volunteer-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='invite-volunteer-modal-content'>
      <div className='header'>
        <button
          type='button'
          className={isChoosingPerson ? 'header-control close' : 'header-control back'}
          disabled={isSaving}
          onClick={() => {
            if (isChoosingPerson) {
              props.close();
              return;
            }

            changePhase('person');
          }}>
          {isChoosingPerson ?
            <img src={closeIcon} alt='Close icon' /> :
            <img src={backIcon} alt='Back icon' />}
        </button>

        <h3 className='title'>
          {isChoosingPerson ? `Invite Volunteer to ${props.team?.attributes.name ?? ''} Team` : 'Select Roles'}
        </h3>

        {isChoosingPerson && hasRolePhase ? <button
          type='button'
          className='header-control forward'
          disabled={!hasInvitee}
          onClick={() => changePhase('roles')}>
          <img src={forwardIcon} alt='Continue icon' />
        </button> : confirmButton}
      </div>

      <div className='body'>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='stage-viewport'>
          {transition ? <div
            className={`stage is-outgoing ${transition.direction}`}
            aria-hidden='true'>
            {renderPhase(transition.from)}
          </div> : null}

          <div className={transition ? `stage is-incoming ${transition.direction}` : 'stage'}>
            {renderPhase(phase)}
          </div>
        </div>
      </div>
    </div>
  </Modal>
}

function SearchResults(props: {
  isSearching: boolean;
  loading: boolean;
  people: Person[];
  selectedPerson: Person | null;
  onSelect: (person: Person) => void;
}) {
  if (!props.isSearching) {
    return null;
  }

  if (props.loading && props.people.length === 0) {
    return <div className='invite-results-state'>
      <LoadingIndicator fullScreen={false} />
    </div>
  }

  if (props.people.length === 0) {
    return <p className='invite-hint'>No one matched that search.</p>
  }

  return <div className='invite-results'>
    {props.people.map((person) => {
      const isSelected = props.selectedPerson?.id === person.id;

      return <div key={person.id} className={isSelected ? 'invite-result is-selected' : 'invite-result'}>
        <PersonCard
          name={person.attributes.name}
          email={person.attributes.email ?? undefined}
          onClick={() => props.onSelect(person)}
        />
      </div>
    })}
  </div>
}
